import { useEffect, useRef } from 'react';
import {
    UI_SCALE_MIN, UI_SCALE_MAX, UI_SCALE_STEP,
    ZOOM_WHEEL_MIN, ZOOM_WHEEL_MAX, ZOOM_WHEEL_STEP,
    PINCH_AXIS_GATE,
    snapUiScale, snapZoom, directionSafe
} from './GanttUtils';

// Wheel zoom, pinch zoom, and the pointer bookkeeping the pan and the drags all
// read. Both charts call this; it owns no state of its own.
//
// ⛔ TWO SCALES, ONE AXIS EACH, AND NOTHING MULTIPLIES THEM.
//
//   ctrl+wheel  / a VERTICAL pinch    -> uiScale   (reading size)
//   shift+wheel / a HORIZONTAL pinch  -> zoomLevel (the time axis)
//   a plain wheel                     -> NOTHING. The browser scrolls.
//
// ⛔ NOT CALLING preventDefault IS THE SINGLE LINE THAT HANDS A PLAIN WHEEL BACK
// TO THE BROWSER. organise-app shipped the plain wheel zooming and Bill reversed
// it after using it. ctrl+wheel and shift+wheel are shortcuts the browser owns -
// page zoom and horizontal scroll - and taking them over this chart is what the
// modifier choice means.
//
// ⛔ THE LISTENER IS NATIVE AND NON-PASSIVE. React registers `wheel` passive at
// its root, so preventDefault inside an onWheel prop is silently ignored. There
// must be no onWheel prop on either chart's JSX.
//
// The hook computes the next scale and hands it back; it never writes scroll
// itself. That is deliberate - the chart owns scrollLeft/scrollTop, and while
// Follow mode runs every scroll write has to stamp its "that was me" window or
// the anchor snaps it back 150ms later.
// ⚠️ Claude's number, like the pinch gate - on the device-walk list, not ruled.
const WHEEL_IDLE_MS = 250;

// A wheel gesture has no end event, so its end is an idle timeout. A pinch has
// a real one (the second finger lifting).
const useGanttGestures = (scrollContainerRef, {
    disabled,
    pointersRef,
    blockedRef,
    zoomRef,
    uiScaleRef,
    onZoom,
    onUiScale,
    onGestureStart,
    onGestureEnd
}) => {
    // ⛔ THE SNAP IS ON THE APPLIED VALUE, NEVER ON THE ONE THE GESTURE
    // ACCUMULATES ON. A trackpad sends a fraction of a percent per event;
    // snapping that would round every one straight back to where it started and
    // the chart would never move at all. So the raw value runs continuously
    // beside the snapped one.
    const rawZoomRef = useRef(null);
    const rawUiRef = useRef(null);
    const wheelIdleRef = useRef(null);
    const pinchRef = useRef(null);

    useEffect(() => {
        const el = scrollContainerRef.current;
        if (!el || disabled) return undefined;

        // ⚠️ touch-action none stops the browser claiming the gesture for its
        // own scrolling; overscroll-behavior contain stops a pan that reaches
        // the top handing the rest to the PAGE, which on a tablet is
        // pull-to-refresh. The second is not optional and is easy to forget,
        // because on a desktop nothing happens without it.
        const priorTouch = el.style.touchAction;
        const priorOverscroll = el.style.overscrollBehavior;
        el.style.touchAction = 'none';
        el.style.overscrollBehavior = 'contain';

        const endSoon = () => {
            if (wheelIdleRef.current) clearTimeout(wheelIdleRef.current);
            wheelIdleRef.current = setTimeout(() => {
                wheelIdleRef.current = null;
                onGestureEnd();
            }, WHEEL_IDLE_MS);
        };

        // A mouse notch is ~100 deltaY and takes a whole step; a trackpad's few
        // pixels take a fraction. ⛔ Firefox reports LINES, not pixels.
        //
        // ⛔ AND shift+wheel ARRIVES AS deltaX ON MOST DESKTOPS. Chrome, Edge
        // and Firefox all translate shift + a vertical notch into a HORIZONTAL
        // scroll delta, so a deltaY-only read leaves shift+wheel completely
        // inert - after preventDefault has already swallowed the scroll it
        // replaced. deltaY is preferred when present (real horizontal-capable
        // wheels, trackpads); deltaX is the same user intent by another field.
        const stepsFrom = (e) => {
            const unit = e.deltaMode === 1 ? 16 : e.deltaMode === 2 ? 100 : 1;
            const delta = e.deltaY !== 0 ? e.deltaY : e.deltaX;
            return (delta * unit) / 100;
        };

        const onWheel = (e) => {
            if (!e.ctrlKey && !e.shiftKey) return;   // plain wheel: browser's
            e.preventDefault();

            const steps = stepsFrom(e);
            // NaN-safe as well as zero-safe: a synthetic event can carry no
            // delta fields at all, and NaN would otherwise flow into the clamp.
            if (!Number.isFinite(steps) || steps === 0) return;
            // deltaY positive is a scroll DOWN, which zooms OUT.
            const outward = steps > 0;

            if (!wheelIdleRef.current) {
                // ⛔ THE RAW VALUES ARE RE-SEEDED FROM THE PROPS AT THE START OF
                // EVERY GESTURE, or a reset leaves a stale one behind: click
                // V.Zoom back to 100% and the next notch must start from 100%,
                // not from wherever the last gesture wandered to. Seeding here
                // rather than watching the values is what makes that exact -
                // the refs the hook is given never change identity, so there is
                // nothing to watch.
                rawUiRef.current = uiScaleRef.current;
                rawZoomRef.current = zoomRef.current;
                onGestureStart();
            }

            if (e.ctrlKey) {
                const from = rawUiRef.current ?? uiScaleRef.current;
                // ⛔ ADDITIVE, never a percentage of the current value: 1.1 x
                // 1.1 is 121%, off the very grid the readout prints, and
                // out-and-back would not land where it started.
                const raw = Math.min(UI_SCALE_MAX, Math.max(UI_SCALE_MIN,
                    from - steps * UI_SCALE_STEP));
                rawUiRef.current = raw;
                const next = directionSafe(uiScaleRef.current, snapUiScale(raw), outward);
                if (next !== uiScaleRef.current) onUiScale(next);
            } else {
                const from = rawZoomRef.current ?? zoomRef.current;
                const raw = Math.min(ZOOM_WHEEL_MAX, Math.max(ZOOM_WHEEL_MIN,
                    from - steps * ZOOM_WHEEL_STEP));
                rawZoomRef.current = raw;
                const next = directionSafe(zoomRef.current, snapZoom(raw), outward);
                if (next !== zoomRef.current) onZoom(next, e.clientX);
            }

            endSoon();
        };

        // ⛔ RECORD EVERY TOUCH POINTER, *THEN* CONSULT THE BLOCK, *THEN* ARM.
        //
        // The map is the single source the pan's abort, the bar drag's abort and
        // the column resize's all read. A bar drag is PRECISELY when the block
        // is on, so returning early here would starve those aborts at the one
        // moment they exist for.
        const onPointerDown = (e) => {
            if (e.pointerType !== 'touch') return;
            pointersRef.current.set(e.pointerId, { x: e.clientX, y: e.clientY });

            if (blockedRef.current) return;
            if (pointersRef.current.size !== 2) return;

            const [a, b] = [...pointersRef.current.values()];
            pinchRef.current = {
                startX: Math.abs(a.x - b.x),
                startY: Math.abs(a.y - b.y),
                startZoom: zoomRef.current,
                startUi: uiScaleRef.current
            };
            rawZoomRef.current = zoomRef.current;
            rawUiRef.current = uiScaleRef.current;
            onGestureStart();
        };

        // ⛔ TWO SEPARATIONS, NOT ONE DISTANCE. The fingers' x spread drives the
        // time axis and their y spread the reading size, each on its own ratio,
        // so a diagonal pinch moves both by different amounts. A single hypot
        // distance cannot express that - it collapses the direction the gesture
        // carries, which is the whole point of it.
        const onPointerMove = (e) => {
            if (e.pointerType !== 'touch') return;
            if (!pointersRef.current.has(e.pointerId)) return;
            pointersRef.current.set(e.pointerId, { x: e.clientX, y: e.clientY });

            const pinch = pinchRef.current;
            if (!pinch || pointersRef.current.size !== 2) return;

            const [a, b] = [...pointersRef.current.values()];
            const spreadX = Math.abs(a.x - b.x);
            const spreadY = Math.abs(a.y - b.y);

            // ⚠️ Each axis is gated on its own START spread. Fingers side by
            // side have almost no y separation, and dividing by that noise turns
            // a 20px move into a 20x zoom. Below the gate that axis is left
            // ENTIRELY ALONE rather than the whole gesture being refused.
            if (pinch.startX >= PINCH_AXIS_GATE) {
                const raw = Math.min(ZOOM_WHEEL_MAX, Math.max(ZOOM_WHEEL_MIN,
                    pinch.startZoom * (spreadX / pinch.startX)));
                rawZoomRef.current = raw;
                const next = snapZoom(raw);
                if (next !== zoomRef.current) onZoom(next, (a.x + b.x) / 2);
            }
            if (pinch.startY >= PINCH_AXIS_GATE) {
                const raw = Math.min(UI_SCALE_MAX, Math.max(UI_SCALE_MIN,
                    pinch.startUi * (spreadY / pinch.startY)));
                rawUiRef.current = raw;
                const next = snapUiScale(raw);
                if (next !== uiScaleRef.current) onUiScale(next);
            }
        };

        const onPointerUp = (e) => {
            if (e.pointerType !== 'touch') return;
            pointersRef.current.delete(e.pointerId);
            if (pinchRef.current && pointersRef.current.size < 2) {
                pinchRef.current = null;
                onGestureEnd();
            }
        };

        // ⛔ iOS SAFARI ALSO FIRES ITS OWN gesture* EVENTS, WHICH touch-action
        // DOES NOT SUPPRESS. Swallowed ON THE ELEMENT and never on document, or
        // the rest of the app loses the user's own page zoom.
        const swallow = (e) => e.preventDefault();

        el.addEventListener('wheel', onWheel, { passive: false });
        el.addEventListener('pointerdown', onPointerDown);
        el.addEventListener('pointermove', onPointerMove);
        el.addEventListener('pointerup', onPointerUp);
        el.addEventListener('pointercancel', onPointerUp);
        el.addEventListener('gesturestart', swallow);
        el.addEventListener('gesturechange', swallow);
        el.addEventListener('gestureend', swallow);

        return () => {
            el.removeEventListener('wheel', onWheel);
            el.removeEventListener('pointerdown', onPointerDown);
            el.removeEventListener('pointermove', onPointerMove);
            el.removeEventListener('pointerup', onPointerUp);
            el.removeEventListener('pointercancel', onPointerUp);
            el.removeEventListener('gesturestart', swallow);
            el.removeEventListener('gesturechange', swallow);
            el.removeEventListener('gestureend', swallow);
            el.style.touchAction = priorTouch;
            el.style.overscrollBehavior = priorOverscroll;
            // ⛔ A gesture still live at teardown is ENDED, wheel OR pinch - the
            // per-change zoom persistence was suppressed for its whole duration,
            // so skipping the end here means the value the user set is never
            // persisted at all. One end call even if both were somehow live.
            const hadLive = wheelIdleRef.current !== null || pinchRef.current !== null;
            if (wheelIdleRef.current) {
                clearTimeout(wheelIdleRef.current);
                wheelIdleRef.current = null;
            }
            pinchRef.current = null;
            if (hadLive) onGestureEnd();
        };
    }, [scrollContainerRef, disabled, pointersRef, blockedRef, zoomRef, uiScaleRef,
        onZoom, onUiScale, onGestureStart, onGestureEnd]);

};

export { useGanttGestures };
